/** @jsxRuntime classic */
/** @jsx jsx */
import { memo, useMemo, useRef, useState, useEffect, forwardRef, Fragment, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { jsx, css } from '@emotion/core';
import { StarHalf, StarOutline } from "../../icons";
import { randomString } from "../../utils";
import { hexToRGBA } from "../../styles/color-helper";
import { useTheme, useColor as colors } from "../../theme";
import { itemsCenter, border, cursorPointer, flexRow, flexRowReverse, displayInlineFlex, outlineNone, overflowHidden, pointerEventsNone, positionAbsolute, positionRelative } from "../../styles/general";
const {
  colors: {
    system: {
      active,
      rest,
      white
    },
    semantic: {
      info
    },
    line: {
      normal
    }
  },
  typography: {
    paragraph3
  },
  spacing
} = useTheme();
const Rating = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  id,
  disabled,
  readOnly,
  half,
  width,
  height,
  selector,
  quantity,
  defaultValue,
  value,
  color,
  className,
  style,
  onRating
}, reference) => {
  if (height === undefined) height = width;
  const ref = useRef(null);
  const [unique] = useState(randomString(6, {
    allowSymbol: false,
    allowNumber: false
  }));
  const [valueState, setValueState] = useState(value || defaultValue);
  const _color = colors[color] || color;
  const _RatingItemHaftCSS = RatingItemHaftCSS(_color);
  const _RatingItemCSS = RatingItemCSS(_RatingItemHaftCSS.name, _color, half);
  const onSelectValue = e => {
    if (disabled || readOnly) return;
    const newValue = +e.target.value;
    e.value = newValue;
    setValueState(newValue);
    !!onRating && onRating(e, newValue);
  };
  const _onRating = (e, value, isHalf = false) => {
    if (disabled || readOnly || half && !isHalf) return;
    setValueState(value);
    !!onRating && onRating(e, value);
  };
  useEffect(() => {
    setValueState(value);
  }, [value]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    const _instance = {}; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  return useMemo(() => {
    let ArrayRating = [];
    [...Array(quantity).keys()].reverse().forEach(item => ArrayRating = [...ArrayRating, item + 1]);
    return jsx("div", {
      id: id,
      css: RatingRootCSS,
      ref: ref,
      style: style,
      className: ['DGN-UI-Rating', className, disabled ? 'disabled' : '', readOnly ? 'read-only' : ''].join(' ').trim().replace(/\s+/g, ' ')
    }, selector && jsx("select", {
      css: RatingSelectCSS,
      name: `DGN-UI-Rating-Select-${unique}`,
      id: `DGN-UI-Rating-Select-${unique}`,
      disabled: disabled,
      value: valueState,
      onChange: onSelectValue
    }, jsx("option", {
      value: '',
      id: unique + '-0'
    }, 0), ArrayRating.map((i, idx) => half ? jsx(Fragment, {
      key: i
    }, jsx("option", {
      value: idx + 0.5
    }, idx + 0.5), jsx("option", {
      value: idx + 1
    }, idx + 1)) : jsx("option", {
      key: i,
      value: idx + 1
    }, idx + 1))), ArrayRating.map(i => jsx("span", {
      css: _RatingItemCSS,
      key: i,
      className: ['DGN-UI-Rating-Item', disabled && 'disabled', i <= valueState && 'chosen'].join(' ').trim().replace(/\s+/g, ' '),
      onClick: e => _onRating(e, i)
    }, jsx(StarOutline, {
      width: width,
      height: height,
      color: disabled ? rest : color || 'currentColor'
    }), jsx("span", {
      css: _RatingItemHaftCSS,
      className: ['DGN-UI-Rating-Item-Star right', disabled && 'disabled', half && 'half', i <= valueState ? 'chosen' : ''].join(' ').trim().replace(/\s+/g, ' '),
      onClick: e => half && _onRating(e, i, true)
    }, jsx(StarHalf, {
      width: width,
      height: height,
      color: 'currentColor'
    })), jsx("span", {
      css: _RatingItemHaftCSS,
      className: ['DGN-UI-Rating-Item-Star left', disabled && 'disabled', half && 'half', i - 0.5 <= valueState ? 'chosen' : ''].join(' ').trim().replace(/\s+/g, ' '),
      onClick: e => half && _onRating(e, i - 0.5, true)
    }, jsx(StarHalf, {
      width: width,
      height: height,
      color: 'currentColor'
    })))));
  }, [half, color, quantity, readOnly, disabled, selector, width, height, onRating, valueState]);
}));
const RatingRootCSS = css`
    ${displayInlineFlex};
    ${flexRowReverse};
    &.disabled,
    &.read-only {
        ${pointerEventsNone};
    }
`;
const RatingSelectCSS = css`
    ${outlineNone};
    ${paragraph3};
    ${border(1, normal)}
    margin-left: ${spacing()}px;
    color: ${active};
    &:hover {
        border-color: ${active};
        background-color: ${hexToRGBA(active, 0.2)};
    }
    &:active,
    &:focus {
        border-color: ${info};
    }
    option {
        background-color: ${white};
    }
`;
const RatingItemHaftCSS = color => css`
    ${flexRow};
    ${positionAbsolute};
    ${overflowHidden};
    width: 50%;
    svg {
        ${cursorPointer}
        color: transparent;
        transition: color 0.15s linear;
        transform: scale(2);
        transform-origin: left;
    }
    &.right {
        transform: rotateY(180deg);
        right: 0;
    }
    &.half {
        svg:hover,
        &.right:hover + span svg,
        &.chosen svg {
            color: ${color};
        }
        &.chosen {
            &.disabled svg {
                color: ${rest} !important;
            }
        }
    }
`;
const RatingItemCSS = (RatingItemHaftCSSName, color, half) => css`
    ${displayInlineFlex};
    ${positionRelative};
    ${itemsCenter};
    svg {
        transition: color 0.15s linear;
    }
    &:hover ~ span > span.css-${RatingItemHaftCSSName}, ${half ? '' : `&:hover,`} &:hover ~ span.full,
    &.chosen ~ span > span.css-${RatingItemHaftCSSName}, ${half ? '' : `&.chosen,`} &.chosen ~ span.full {
        svg {
            color: ${color};
        }
    }
    &.disabled {
        &.chosen {
            svg {
                color: ${rest} !important;
            }
        }
    }
`;
Rating.defaultProps = {
  disabled: false,
  readOnly: false,
  half: false,
  selector: false,
  width: 20,
  quantity: 5,
  defaultValue: 0,
  color: 'primary',
  className: ''
};
Rating.propTypes = {
  /** If true, the component is disabled. */
  disabled: PropTypes.bool,
  /** Removes all hover effects and pointer events. */
  readOnly: PropTypes.bool,
  /** Allows rating with an odd number of stars. */
  half: PropTypes.bool,
  /** Display select box to select by number. */
  selector: PropTypes.bool,
  /** The default number of stars is selected. */
  defaultValue: PropTypes.number,
  /** The number of stars is selected. */
  value: PropTypes.number,
  /** The maximum number of stars allowed to rate. */
  quantity: PropTypes.number,
  /** Width of star icon. */
  width: PropTypes.number,
  /** Height of star icon. */
  height: PropTypes.number,
  /** Color of stars, ['primary', 'info', 'success', 'warning', 'danger', #hex, rgb(#, #, #)]. */
  color: PropTypes.string,
  /** Class for component. */
  className: PropTypes.string,
  /** Style inline of component. */
  style: PropTypes.object,
  /**
  * Callback fired when the value changes.
  * 
    function(event: React.SyntheticEvent, value: number) => void
  *
  * event: The event source of the callback.
  * 
  * value: The new value.
  */
  onRating: PropTypes.func
};
export default Rating;